/* eslint-disable react-hooks/exhaustive-deps */
import {Pagination} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";

import {queryLimit} from "../../constants";
import usePageNavigation from "../../hooks/usePageNavigation";

import {getAllMyReceipts} from "../../services/requests/ApiRequests";
import {valueToFixed} from "../../services/toFixed";

import {getQueryParams} from "../../utils";
import {CONSTANT_PAYMENT_METHODS_DICT} from "../constants";
import CashPicker from "./CashPicker";
import DatePickerRange from "./DatePicker";

export default function CashLogs(props) {
	let navigate = useNavigate();
	const {t} = useTranslation();
	const {pageNumber = "1"} = useParams();
	const [journals, setJournals] = useState([]);
	const [loading, setLoading] = useState(false);
	const [sortingDates, setSortingDates] = useState({
		dateFrom: null,
		dateTo: null,
	});
	const [idRange, setIdRange] = useState({idFrom: "", idTo: ""});
	const [totalRange, setTotalRange] = useState({
		totalFrom: "",
		totalTo: "",
	});
	const [currentPage, setCurrentPage] = useState(1);
	const [pageCount, setPageCount] = useState(null);
	const [cashStart, setCashStart] = useState(0);
	const [isCashRegisterAvailable, setIsCashRegisterAvailable] =
		useState(null);
	const [card, setCard] = useState({});
	const [selectedCashRegisterId, setSelectedCashRegisterId] = useState(null);
	const redirectLink = "acps/cash-logs";

	usePageNavigation(
		setCurrentPage,
		setCashStart,
		pageNumber,
		queryLimit,
		pageCount,
		navigate,
		redirectLink
	);

	const onSubmitCashRegisterForm = (data) => {
		setCard(data.formData);
		setIsCashRegisterAvailable(!!data.organization);
	};

	const handlePagination = (event, page) => {
		navigate(`/${redirectLink}/${page}`);
	};

	const getReceipts = () => {
		if (!isCashRegisterAvailable) return;
		setLoading(true);
		const reqBody = {
			...card,
			limit: queryLimit,
			start: cashStart,
			date_from: sortingDates.dateFrom,
			date_to: sortingDates.dateTo,
			id_from: idRange.idFrom || undefined,
			id_to: idRange.idTo || undefined,
			total_from: totalRange.totalFrom || undefined,
			total_to: totalRange.totalTo || undefined,
		};
		getAllMyReceipts(reqBody)
			.then((data) => {
				const {data: resData, count: resCount} = data.data;
				setJournals(resData);
				setPageCount(Math.ceil(resCount / queryLimit));
				setLoading(false);
			})
			.catch(() => setLoading(false));
	};

	useEffect(() => {
		getReceipts();
	}, [
		JSON.stringify(card),
		JSON.stringify(sortingDates),
		JSON.stringify(idRange),
		JSON.stringify(totalRange),
		currentPage,
		isCashRegisterAvailable,
	]);

	const queryParams = getQueryParams(card);

	const getFromIdOptions = () => {
		if (!idRange.idTo) return journals;
		const to = Number(idRange.idTo);
		return journals.filter((r) => r._id <= to);
	};

	const getToIdOptions = () => {
		if (!idRange.idFrom) return journals;
		const from = Number(idRange.idFrom);
		return journals.filter((r) => r._id >= from);
	};

	const formatTotal = (value) => {
		const num = parseFloat(value);
		if (Number.isNaN(num)) return value ?? "";
		return num.toFixed(2);
	};

	return (
		<Box sx={{flexGrow: 1, marginTop: 3, marginLeft: 0, marginRight: 3}}>
			<CashPicker
				onSubmit={onSubmitCashRegisterForm}
				setSelectedCashRegister={(cash) =>
					setSelectedCashRegisterId(cash?._id ?? null)
				}
			/>
			<Grid
				container
				spacing={2}
				alignItems="center"
				sx={{marginTop: 3}}
			>
				<Grid item xs={3} align="center">
					<DatePickerRange
						setSortingDates={setSortingDates}
						isCashSelected={!!isCashRegisterAvailable}
					/>
				</Grid>
				<Grid item xs={2} align="center">
					<Autocomplete
						options={getFromIdOptions()}
						getOptionLabel={(r) => (r ? String(r._id) : "")}
						value={
							journals.find(
								(r) => String(r._id) === String(idRange.idFrom)
							) || null
						}
						onChange={(_, newValue) =>
							setIdRange((prev) => ({
								...prev,
								idFrom: newValue?._id || "",
							}))
						}
						renderInput={(params) => (
							<TextField
								{...params}
								label={t("from")}
								helperText={t("select_first_receipt")}
								variant="standard"
								fullWidth
								sx={{m: 1}}
							/>
						)}
						isOptionEqualToValue={(option, value) =>
							option._id === value._id
						}
						disabled={
							!isCashRegisterAvailable || journals.length === 0
						}
					/>
				</Grid>
				<Grid item xs={2} align="center">
					<Autocomplete
						options={getToIdOptions()}
						getOptionLabel={(r) => (r ? String(r._id) : "")}
						value={
							journals.find(
								(r) => String(r._id) === String(idRange.idTo)
							) || null
						}
						onChange={(_, newValue) =>
							setIdRange((prev) => ({
								...prev,
								idTo: newValue?._id || "",
							}))
						}
						renderInput={(params) => (
							<TextField
								{...params}
								label={t("to")}
								helperText={t("select_last_receipt")}
								variant="standard"
								fullWidth
								sx={{m: 1}}
							/>
						)}
						isOptionEqualToValue={(option, value) =>
							option._id === value._id
						}
						disabled={
							!isCashRegisterAvailable || journals.length === 0
						}
					/>
				</Grid>
				<Grid item xs={2} align="center">
					<TextField
						label={`${t("total")} ${t("from")}`}
						value={totalRange.totalFrom}
						onChange={(e) => {
							const raw = e.target.value.replace(/,/g, ".");
							const cleaned = raw.replace(/[^0-9.]/g, "");
							const formatted =
								cleaned === "" ? "" : valueToFixed(cleaned);
							setTotalRange((prev) => ({
								...prev,
								totalFrom: formatted,
							}));
						}}
						helperText={t("sum_from")}
						fullWidth
						sx={{m: 1}}
						variant="standard"
						inputProps={{inputMode: "decimal"}}
						disabled={!isCashRegisterAvailable}
					/>
				</Grid>
				<Grid item xs={2} align="center">
					<TextField
						label={`${t("total")} ${t("to")}`}
						value={totalRange.totalTo}
						onChange={(e) => {
							const raw = e.target.value.replace(/,/g, ".");
							const cleaned = raw.replace(/[^0-9.]/g, "");
							const formatted =
								cleaned === "" ? "" : valueToFixed(cleaned);
							setTotalRange((prev) => ({
								...prev,
								totalTo: formatted,
							}));
						}}
						helperText={t("sum_to")}
						fullWidth
						sx={{m: 1}}
						variant="standard"
						inputProps={{inputMode: "decimal"}}
						disabled={!isCashRegisterAvailable}
					/>
				</Grid>
			</Grid>
			{/* <CashRegisterForm onSubmit={onSubmitCashRegisterForm} /> */}
			<Grid container spacing={2} align="left" sx={{marginTop: 3}}>
				<Grid item xs={12}>
					<TableContainer component={Paper}>
						<Table sx={{minWidth: 650}} aria-label="simple table">
							<TableHead>
								<TableRow>
									<TableCell>{t("id")}</TableCell>
									<TableCell align="right">
										{t("receipt")} #
									</TableCell>
									<TableCell align="right">
										{t("payment_type")}
									</TableCell>
									<TableCell align="right">
										{t("date_time")}
									</TableCell>
									<TableCell align="right">
										{t("total")}(MDL)
									</TableCell>
									<TableCell align="right">
										{t("cashier_name")}
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loading ? (
									<TableRow>
										<TableCell colSpan={8}>
											<div align="center">
												<CircularProgress size={35}/>
											</div>
										</TableCell>
									</TableRow>
								) : (
									journals.map((receipt) => {
										if (receipt.status === "synced") {
											return (
												<TableRow
													hover
													key={receipt.soap_data.id}
													sx={{
														"&:last-child td, &:last-child th":
															{border: 0},
													}}
													onClick={() => {
														const rid =
															selectedCashRegisterId ||
															receipt.cash_register_id;
														if (rid) {
															navigate(
																`/acps/receipt/${receipt._id}?rid=${rid}`
															);
														} else {
															navigate(
																`/acps/receipt/${receipt._id}`
															);
														}
													}}
												>
													<TableCell align="left">
														{receipt.soap_data.id}
													</TableCell>
													<TableCell align="right">
														{
															receipt.soap_data
																.receipt
														}
													</TableCell>
													<TableCell align="right">
														{t(
															CONSTANT_PAYMENT_METHODS_DICT[
																receipt
																	.soap_data
																	.data
																	.payments
																	.field[0]
																	.type
																].methodName
														)}
													</TableCell>
													<TableCell align="right">
														{
															receipt.soap_data
																.datetime
														}
													</TableCell>
													<TableCell align="right">
														{formatTotal(
															receipt.soap_data
																.data.total
														)}
													</TableCell>
													<TableCell align="right">
														{receipt.user &&
														receipt.user.login
															? receipt.user.login
															: " "}
													</TableCell>
												</TableRow>
											);
										} else {
											return (
												<TableRow
													hover
													onClick={() => {
														const rid =
															selectedCashRegisterId ||
															receipt.cash_register_id;
														const qs = rid
															? `rid=${rid}&${queryParams}`
															: queryParams;
														navigate(
															`/acps/receipt/${receipt._id}?${qs}`
														);
													}}
													key={receipt.soap_data.id}
													sx={{
														"&:last-child td, &:last-child th":
															{border: 0},
													}}
												>
													<TableCell align="left">
														{receipt.soap_data.id}
													</TableCell>
													<TableCell align="right">
														{
															receipt.soap_data
																.receipt
														}
													</TableCell>
													<TableCell align="right">
														{t(
															CONSTANT_PAYMENT_METHODS_DICT[
																receipt
																	.soap_data
																	.data
																	.payments
																	.field[0]
																	.type
																].methodName
														)}
													</TableCell>
													<TableCell align="right">
														{
															receipt.soap_data
																.datetime
														}
													</TableCell>
													<TableCell align="right">
														{
															receipt.soap_data
																.data.total
														}
													</TableCell>
													{/*<TableCell align="right">*/}
													{/*	{receipt.status}*/}
													{/*</TableCell>*/}
													{/*<TableCell align="right">*/}
													{/*	{receipt.cashier &&*/}
													{/*	receipt.cashier.id !==*/}
													{/*	null*/}
													{/*		? receipt.cashier.id*/}
													{/*		: " "}*/}
													{/*</TableCell>*/}
													<TableCell align="right">
														{receipt.user &&
														receipt.user.login
															? receipt.user.login
															: " "}
													</TableCell>
												</TableRow>
											);
										}
									})
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</Grid>
				{pageCount > 1 && (
					<Grid item xs={11} align="center">
						<Pagination
							sx={{
								my: 5,
								justifyContent: "center",
								display: "flex",
							}}
							count={pageCount}
							variant="outlined"
							shape="rounded"
							page={+currentPage}
							onChange={handlePagination}
						/>
					</Grid>
				)}
			</Grid>
		</Box>
	);
}
